import { IconCheck, IconEdit, IconEye, IconTrash } from "@tabler/icons";
import { FC, useState, useEffect } from "react";
import {
  Button,
  Tag,
  previewFunc,
  UtilityModal,
  ToastNotification,
  ModalBody,
} from "..";
import badge from "../../assets/images/badge.png";
import { DELETE_CUSTOM_EMAIL_TEMPLATE } from "../../graphql/mutation/emailTemplate";
import { useMutation } from "@apollo/client";
import { getStringValue, isTemplateIncludedInPlan } from "../../utils";
import EditTemplateModal, { TemplateData } from "../EditTemplateModal";
import UpgradeModal from "../../modules/Campaigns/components/modals/UpgradeModal";

interface TemplateCardProps {
  id: string;
  siteId?: string | number | null;
  isActive: boolean;
  name: string;
  tags: Array<string>;
  screenshotUrl: string;
  premium: boolean;
  hasNoEntitlement: boolean;
  className?: string;
  width?: number;
  height?: number;
  designFormat?: string;
  subject?: string;
  preheader?: string;
  onClick: (
    id: string,
    designFormat?: string,
    templateName?: string,
    subject?: string,
    preheader?: string,
    utm?: any,
    templateSiteId?: string | number | null
  ) => void;
  onTemplateUpdate?: (updatedTemplate: TemplateData) => void;
  live?: boolean;
  utm?: any;
  plans?: any;
  type?: string;
  direction?: 'ltr' | 'rtl';
  isEmail?: boolean;
}

const DeleteCustomUserTemplateModal = ({
  id,
  siteId,
  name,
  handleClose,
}: {
  id: string;
  siteId: string;
  name: string;
  handleClose: () => void;
}) => {
  const [onDeleteTemplate] = useMutation(DELETE_CUSTOM_EMAIL_TEMPLATE, {
    variables: {
      input: {
        id: parseInt(id),
        siteId: parseInt(siteId),
      },
    },
    update(cache) {
      cache.evict({
        id: cache.identify({
          __typename: "EmailTemplate",
          id: parseInt(id),
        }),
      });
    },
    onCompleted: ({ deleteCustomEmailTemplate: { success = false } }) => {
      handleClose();
      ToastNotification({
        id: new Date(),
        icon: success ? "check" : "alert",
        color: success ? "success" : "danger",
        body: success
          ? "Template Deleted successfully"
          : "There is an Error. Please Try again Later",
      });
    },
  });

  return (
    <ModalBody
      action="danger"
      title="Are you sure you want to delete?"
      description={
        <p>
          This will delete your Custom Template <b>{name}</b> and this action
          cannot be reverted
        </p>
      }
      cancelButtonText="DISCARD"
      cancelButton={true}
      handleClose={handleClose}
      buttons={[
        {
          label: "Confirm",
          action: "danger",
          onClick: onDeleteTemplate,
        },
      ]}
    />
  );
};

export const TemplateCard: FC<TemplateCardProps> = (props) => {
  const {
    id,
    isActive,
    name,
    className,
    onClick,
    tags,
    screenshotUrl,
    premium,
    hasNoEntitlement,
    siteId,
    designFormat,
    live = true,
    width = 244,
    height = 206,
    subject = "",
    preheader = "",
    utm = {},
    plans = null,
    type,
    direction,
    onTemplateUpdate,
    isEmail,
  } = props;

  const tagList = tags?.map((tag, index) => (
    <Tag
      reactIntlId={tag}
      action="default"
      key={index}
      className="mr-2 whitespace-nowrap mt-2"
      labelClassName="capitalize"
      label={tag.split("_").join(" ")}
    />
  ));

  const isTemplateUserDefined = tags.includes("usertemplates");

  const [localName, setLocalName] = useState(name);

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [templateData, setTemplateData] = useState<any>(null);

  const handleUseTemplate = () => {
    if (siteId && !(window as any)?.entitlements?.saved_email_templates) {
      UpgradeModal({ isSiteOwner: (window as any)?.isSiteOwner })
      return
    }

    onClick(id, designFormat, localName, subject, preheader, utm, siteId)
  }

  
  useEffect(() => {
    setLocalName(name);
  }, [name]);

  const handleEditClick = () => {
    if (!siteId) {
      ToastNotification({
        icon: "alert",
        color: "danger",
        body: "Site ID is missing. Cannot edit template.",
        holdTime: 3000,
      });
      return;
    }

    const currentTemplateData = {
      id,
      siteId,
      name: localName,
      subject,
      preheader,
      designFormat,
      tags: tags.filter((tag) => tag !== "usertemplates"),
    };

    setTemplateData(currentTemplateData);
    setIsEditModalOpen(true);
  };

  return (
    <div className={`mb-[24px] relative ${!live && "opacity-50"}`}>
      <div
        className={`relative w-max group ${className}`}
        data-attribute={`template-${getStringValue(localName)}`}
      >
        <div
          style={{
            width,
            height,
            backgroundImage: `url(${screenshotUrl})`,
          }}
          className={`${
            isActive && "border-[1px] border-primary"
          } bg-center bg-contain bg-no-repeat rounded-[8px] bg-gray-400 mb-[8px]`}
        >
          <div className="group-hover:bg-black group-hover:opacity-60 w-full h-full rounded-lg" />
          {isActive && (
            <div className="flex z-[2] items-center justify-center rounded-full bg-primary w-6 h-6 absolute -right-3 -top-3">
              <IconCheck size="16px" className="text-white" />
            </div>
          )}
        </div>

        {type !== "automation" &&
          premium &&
          !isTemplateIncludedInPlan(plans, hasNoEntitlement) && (
            <div className="absolute top-0 left-0">
              <img className="w-[50%]" src={badge} alt="badge" />
            </div>
          )}

        {direction === 'rtl' && (
          <div className="absolute top-2 right-4">
            <Tag action="default" label="RTL" className="bg-primary-lighter" />
          </div>
        )}

        <div className="group-hover:flex hidden absolute inset-0 flex-col items-center justify-center">
          <Button
            reactIntlId="builder.template.use_template.button"
            label={"USE TEMPLATE"}
            className="mb-[10px]"
            onClick={handleUseTemplate}
            dataAttribute="use-template"
          />

          {isTemplateUserDefined ? (
            <div className="flex items-center justify-center space-x-3 mt-2">
              
              <button
                className="w-10 h-10 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center transition-colors"
                onClick={() =>
                  previewFunc({
                    id,
                    canEditName: !!siteId,
                    title: localName,
                    url: screenshotUrl.replace("screenshot", "preview_html"),
                    hasPreview: true,
                    isTemplatePreview: true,
                    tags: tagList,
                    onClick: handleUseTemplate,
                    isEmail,
                  })
                }
                title="Preview Template"
              >
                <IconEye size="16px" className="text-gray-600" />
              </button>

              
              <button
                className="w-10 h-10 rounded-full bg-blue-100 hover:bg-blue-200 border-2 border-blue-500 flex items-center justify-center transition-colors"
                onClick={handleEditClick}
                title="Edit Template"
              >
                <IconEdit size="16px" className="text-blue-600" />
              </button>

              
              <button
                className="w-10 h-10 rounded-full bg-red-100 hover:bg-red-200 flex items-center justify-center transition-colors"
                onClick={() =>
                  UtilityModal({
                    body: ({ handleClose }) => (
                      <DeleteCustomUserTemplateModal
                        siteId={String(siteId)}
                        id={id}
                        name={localName}
                        handleClose={handleClose}
                      />
                    ),
                  })
                }
                title="Delete Template"
              >
                <IconTrash size="16px" className="text-red-600" />
              </button>
            </div>
          ) : (
            <div className="flex items-center justify-center space-x-3 mt-2">
              <button
                className="w-10 h-10 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center transition-colors"
                onClick={() =>
                  previewFunc({
                    id,
                    canEditName: !!siteId,
                    title: localName,
                    url: screenshotUrl.replace("screenshot", "preview_html"),
                    hasPreview: true,
                    isTemplatePreview: true,
                    tags: tagList,
                    onClick: handleUseTemplate,
                    isEmail,
                  })
                }
                title="Preview Template"
              >
                <IconEye size="16px" className="text-gray-600" />
              </button>
            </div>
          )}
        </div>
      </div>

    
      <div className="flex items-center">
        <p className="text-lg font-medium overflow-ellipsis whitespace-nowrap overflow-hidden w-[244px] text-secondary mr-[14px]">
          {localName || `User Template`}
        </p>
      </div>

      
      
<EditTemplateModal
  key={templateData?.id || "new"}
  isOpen={isEditModalOpen}
  onClose={() => setIsEditModalOpen(false)}
  templateData={templateData}
  onSave={(updatedData) => {

    setLocalName(updatedData.name);
    setTemplateData(updatedData);

    
    if (onTemplateUpdate && typeof onTemplateUpdate === "function") {
      onTemplateUpdate(updatedData);
    }
    
    
    setIsEditModalOpen(false);
  }}
/>

    </div>
  );
};
